import { useState } from 'react'
import type { BehaviorSettings, BehaviorSettingsInput, BehaviorsState } from '../index.js'
import type { ScorePreview, TierCounts } from '../../scoring/index.js'
import type { WorkerTier } from '../../core/index.js'
import type { BehaviorsApi } from './behaviorsApi.js'
import type { BehaviorsScope } from './BehaviorsManager.js'
import { TIER_LABELS } from '../../core/index.js'
import { Button } from '../../core/index.js'
import { Eye } from 'lucide-react'

const TIER_ORDER: readonly WorkerTier[] = ['gold', 'silver', 'bronze', 'probationary']

function describeTiers(counts: TierCounts): string {
  return TIER_ORDER.filter((tier) => counts[tier] > 0)
    .map((tier) => `${counts[tier]} ${TIER_LABELS[tier]}`)
    .join(', ')
}

/**
 * The settings each facility would run under if the draft were saved. For
 * a facility's own draft that is the draft itself. For a master draft, a
 * facility keeps whatever it overrides and takes the draft for the rest -
 * which is exactly what saving the master would do.
 */
function requestsFor(state: BehaviorsState, scope: BehaviorsScope, draft: BehaviorSettings): { facilityId: string; name: string; behaviors: BehaviorSettingsInput }[] {
  if (scope.kind === 'facility') {
    const facility = state.facilities.find((candidate) => candidate.facilityId === scope.facilityId)
    return facility ? [{ facilityId: facility.facilityId, name: facility.name, behaviors: draft }] : []
  }
  return state.facilities.map((facility) => ({
    facilityId: facility.facilityId,
    name: facility.name,
    behaviors: Object.fromEntries(Object.entries(draft).filter(([key]) => !(key in facility.overrides))),
  }))
}

type Outcome = { name: string; preview: ScorePreview } | { name: string; error: string }

interface BehaviorsPreviewProps {
  api: BehaviorsApi
  state: BehaviorsState
  scope: BehaviorsScope
  draft: BehaviorSettings
}

/**
 * "What would this change do?" for an unsaved behaviors draft: runs the
 * scoring engine over every affected facility's roster with the draft in
 * place of the saved settings, and shows who would move tier. Nothing is
 * stored; the answer is the facility's current roster and outcomes scored
 * two ways.
 */
export function BehaviorsPreview({ api, state, scope, draft }: BehaviorsPreviewProps) {
  const [outcomes, setOutcomes] = useState<Outcome[] | null>(null)
  const [isRunning, setIsRunning] = useState(false)

  async function run(): Promise<void> {
    setIsRunning(true)
    try {
      const results = await Promise.all(
        requestsFor(state, scope, draft).map(async ({ facilityId, name, behaviors }): Promise<Outcome> => {
          try {
            return { name, preview: await api.preview(facilityId, { behaviors }) }
          } catch (err) {
            return { name, error: err instanceof Error && err.message !== '' ? err.message : 'The preview failed' }
          }
        }),
      )
      setOutcomes(results)
    } finally {
      setIsRunning(false)
    }
  }

  return (
    <div className="mt-4 border border-dashed border-marine/30 rounded p-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-[11px] text-ink-mid">
          <span className="font-bold text-ink">What would this change do?</span> Scores every worker with these settings instead of the saved ones, storing nothing.
        </div>
        <Button icon={Eye} variant="secondary" size="sm" disabled={isRunning} onClick={() => void run()}>
          {isRunning ? 'Previewing…' : outcomes ? 'Preview again' : 'Preview'}
        </Button>
      </div>
      {outcomes && (
        <ul className="mt-3 space-y-2">
          {outcomes.map((outcome) => (
            <li key={outcome.name} className="text-[11px]">
              <span className="font-bold text-ink">{outcome.name}</span>
              {'error' in outcome ? (
                <span className="ml-2 text-coral">{outcome.error}</span>
              ) : (
                <>
                  <span className="ml-2">
                    <span className="font-bold text-green">{outcome.preview.summary.up} up</span> · <span className="font-bold text-coral">{outcome.preview.summary.down} down</span> ·{' '}
                    {outcome.preview.summary.unchanged} unchanged
                    {outcome.preview.summary.unscored > 0 && ` · ${outcome.preview.summary.unscored} with nothing to score`}
                  </span>
                  <div className="text-ink-mid">
                    {describeTiers(outcome.preview.summary.currentTiers) || 'no one'} → {describeTiers(outcome.preview.summary.previewTiers) || 'no one'}
                  </div>
                  {outcome.preview.workers.filter((worker) => worker.previewTier !== null && worker.currentTier !== worker.previewTier).length > 0 && (
                    <div className="text-ink-mid">
                      {outcome.preview.workers
                        .filter((worker) => worker.previewTier !== null && worker.currentTier !== worker.previewTier)
                        .map((worker) => `${worker.name}: ${worker.currentTier ? TIER_LABELS[worker.currentTier] : '—'} → ${TIER_LABELS[worker.previewTier as WorkerTier]}`)
                        .join(' · ')}
                    </div>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
